import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Image, Plus, X, Save, ChevronDown, Target } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from './AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminMockupTemplates = () => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  const [form, setForm] = useState({
    name: '', garment_color: 'white', view: 'front',
    zone_x: 200, zone_y: 250, zone_max_width: 120, zone_max_height: 120, zone_name: 'left_chest',
  });

  const headers = { Authorization: `Bearer ${token}` };

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/design/mockup-templates`, { headers });
      setTemplates(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) loadTemplates(); }, [token]);

  const handleUpload = async () => {
    const file = fileRef.current?.files[0];
    if (!file || !form.name) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await axios.post(`${API}/api/design/admin/mockup-templates`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setShowUpload(false);
      setForm({ name: '', garment_color: 'white', view: 'front', zone_x: 200, zone_y: 250, zone_max_width: 120, zone_max_height: 120, zone_name: 'left_chest' });
      setPreview(null);
      loadTemplates();
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await axios.delete(`${API}/api/design/admin/mockup-templates/${id}`, { headers });
      loadTemplates();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-mockup-templates">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1B3A5C]">Mockup Templates</h1>
            <p className="text-sm text-gray-400 mt-1">Upload blank garment images for design mockup rendering</p>
          </div>
          <button onClick={() => setShowUpload(!showUpload)}
            className="flex items-center gap-2 px-4 py-2 bg-[#1B3A5C] text-white rounded-xl text-sm font-semibold hover:bg-[#152e4a] transition"
            data-testid="add-template-btn">
            {showUpload ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showUpload ? 'Cancel' : 'Add Template'}
          </button>
        </div>

        {/* Upload Form */}
        {showUpload && (
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-6 border border-gray-100" data-testid="upload-form">
            <h2 className="text-lg font-bold text-[#1B3A5C] mb-4">Upload New Template</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Image upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Garment Image</label>
                <div
                  onClick={() => fileRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-[#4A90A4] transition"
                  data-testid="file-drop-zone">
                  {preview ? (
                    <img src={preview} alt="Preview" className="max-h-60 mx-auto rounded-lg" />
                  ) : (
                    <div className="py-8">
                      <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-400">Click to upload a blank garment image</p>
                      <p className="text-xs text-gray-300 mt-1">PNG, JPG recommended — high resolution preferred</p>
                    </div>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange}
                  data-testid="file-input" />
              </div>

              {/* Right: Form fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Template Name *</label>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., Polo Shirt — White — Front"
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#4A90A4]"
                    data-testid="template-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Garment Color</label>
                    <input type="text" value={form.garment_color} onChange={e => setForm({ ...form, garment_color: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#4A90A4]"
                      data-testid="garment-color-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">View</label>
                    <select value={form.view} onChange={e => setForm({ ...form, view: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#4A90A4]"
                      data-testid="view-select">
                      <option value="front">Front</option>
                      <option value="back">Back</option>
                      <option value="left_sleeve">Left Sleeve</option>
                      <option value="right_sleeve">Right Sleeve</option>
                    </select>
                  </div>
                </div>

                {/* Placement Zone */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-[#4A90A4]" />
                    <span className="text-sm font-semibold text-gray-700">Placement Zone</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Zone Name</label>
                      <select value={form.zone_name} onChange={e => setForm({ ...form, zone_name: e.target.value })}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
                        data-testid="zone-name-select">
                        <option value="left_chest">Left Chest</option>
                        <option value="full_front">Full Front</option>
                        <option value="full_back">Full Back</option>
                        <option value="left_sleeve">Left Sleeve</option>
                        <option value="right_sleeve">Right Sleeve</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Center X (px)</label>
                      <input type="number" value={form.zone_x} onChange={e => setForm({ ...form, zone_x: parseInt(e.target.value) || 0 })}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm" data-testid="zone-x-input" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Center Y (px)</label>
                      <input type="number" value={form.zone_y} onChange={e => setForm({ ...form, zone_y: parseInt(e.target.value) || 0 })}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm" data-testid="zone-y-input" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Max Width (px)</label>
                      <input type="number" value={form.zone_max_width} onChange={e => setForm({ ...form, zone_max_width: parseInt(e.target.value) || 0 })}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm" data-testid="zone-width-input" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Max Height (px)</label>
                      <input type="number" value={form.zone_max_height} onChange={e => setForm({ ...form, zone_max_height: parseInt(e.target.value) || 0 })}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm" data-testid="zone-height-input" />
                    </div>
                  </div>
                </div>

                <button onClick={handleUpload} disabled={uploading || !form.name || !fileRef.current?.files[0]}
                  className="w-full py-2.5 bg-[#4A90A4] text-white rounded-xl font-semibold text-sm hover:bg-[#3d7a8e] disabled:opacity-40 transition"
                  data-testid="upload-template-btn">
                  {uploading ? 'Uploading...' : 'Upload Template'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Templates Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-[#4A90A4] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : templates.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 shadow-sm text-center" data-testid="empty-templates">
            <Image className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No Mockup Templates</h3>
            <p className="text-sm text-gray-400 max-w-md mx-auto">
              Upload blank garment images to enable customers to preview their embroidery designs on real products.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
            {templates.map(t => (
              <div key={t.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden group"
                data-testid={`template-card-${t.id}`}>
                <div className="aspect-[4/5] bg-gray-50 relative">
                  <img src={`${API}/api/${t.image_url?.replace(/^\/api\//, '')}`} alt={t.name}
                    className="w-full h-full object-contain p-2"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                  {/* Zone overlay */}
                  {t.zones?.[0] && (
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute border-2 border-dashed border-[#4A90A4]/40 bg-[#4A90A4]/5 rounded"
                        style={{
                          left: `${((t.zones[0].x - t.zones[0].max_width / 2) / 600) * 100}%`,
                          top: `${((t.zones[0].y - t.zones[0].max_height / 2) / 800) * 100}%`,
                          width: `${(t.zones[0].max_width / 600) * 100}%`,
                          height: `${(t.zones[0].max_height / 800) * 100}%`,
                        }} />
                    </div>
                  )}
                </div>
                <div className="p-3 border-t border-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm text-[#1B3A5C]">{t.name}</p>
                      <p className="text-xs text-gray-400">{t.garment_color} / {t.view} / {t.zones?.[0]?.name || 'default'}</p>
                    </div>
                    <button onClick={() => handleDelete(t.id, t.name)}
                      className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                      data-testid={`delete-template-${t.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMockupTemplates;
